import { type RowData } from '@tanstack/react-table';
import { type LegacyRow as Row } from '@tanstack/react-table/legacy';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useEffect, useRef } from 'react';

type UseTableRowVirtualizerProps<TData extends RowData> = {
  rows: Row<TData>[];
  isLoadingMore: boolean;
  tableContainer: HTMLDivElement | null;
  loadMore: () => void;
  hasMoreToLoad: boolean;
  infiniteScroll?: boolean;
};

export const useTableRowVirtualizer = <TData extends RowData>({
  rows,
  infiniteScroll,
  isLoadingMore,
  tableContainer,
  loadMore,
  hasMoreToLoad,
}: UseTableRowVirtualizerProps<TData>) => {
  const loadMoreLockRef = useRef(false);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    enabled: infiniteScroll,
    estimateSize: () => 57, //estimate row height for accurate scrollbar dragging
    getScrollElement: () => tableContainer,
    //measure dynamic row height, except in firefox because it measures table border height incorrectly
    measureElement:
      typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element.getBoundingClientRect().height
        : undefined,
    overscan: 15,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    if (!isLoadingMore) {
      loadMoreLockRef.current = false;
    }
  }, [isLoadingMore, rows.length]);

  useEffect(() => {
    if (virtualRows.length === 0) {
      return;
    }

    const lastVirtualRow = virtualRows[virtualRows.length - 1];

    if (!hasMoreToLoad || isLoadingMore || loadMoreLockRef.current) {
      return;
    }

    const threshold = 5;
    const reachedEnd = lastVirtualRow.index >= rows.length - 1 - threshold;

    if (reachedEnd) {
      loadMoreLockRef.current = true;
      loadMore();
    }
  }, [virtualRows, rows.length, loadMore, isLoadingMore, hasMoreToLoad]);

  const paddingTop = virtualRows[0]?.start ?? 0;
  const paddingBottom =
    virtualRows.length > 0
      ? Math.max(0, rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end)
      : 0;

  return {
    rowVirtualizer,
    virtualRows,
    paddingTop,
    paddingBottom,
  };
};
